import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  api,
  User,
  Project,
  Group,
  UserPermission,
  PermissionLevel,
} from '../../api';
import { getErrorMessage } from '../../../../shared/src';
import { PermissionSelector } from '../ui/PermissionSelector';
import { ExpandableResourceCell, ExpandableCellItem } from './ExpandableResourceCell';

type Column = 'groups' | 'projects';

interface ExpandedCell {
  userId: string;
  column: Column;
}

interface UserCache {
  groups?: Group[];
  permissions?: UserPermission[];
}

export function PermissionsUsersSubTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [expandedCell, setExpandedCell] = useState<ExpandedCell | null>(null);
  const [userCache, setUserCache] = useState<Record<string, UserCache>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Single round-trip: the server pre-joins each user's groups and
      // permissions, replacing the old 1 + 2N request fan-out.
      const overview = await api.getUsersPermissionsOverview();
      setUsers(overview.users);
      setAllGroups(overview.groups);
      setProjects(overview.projects);
      setUserCache(
        Object.fromEntries(
          overview.users.map((u) => [
            u.id,
            {
              groups: overview.userGroups[u.id] ?? [],
              permissions: overview.userPermissions[u.id] ?? [],
            },
          ])
        )
      );
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCell = (userId: string, column: Column) => {
    if (expandedCell && expandedCell.userId === userId && expandedCell.column === column) {
      setExpandedCell(null);
      return;
    }
    setExpandedCell({ userId, column });
  };

  const handleToggleMembership = async (userId: string, groupId: string, isCurrentlyMember: boolean) => {
    setUpdatingKey(`group-${groupId}`);
    try {
      // Per-member add/remove (not a replace-all PUT): independent, idempotent
      // ops so toggling several groups in quick succession can't clobber each
      // other.
      if (isCurrentlyMember) {
        await api.removeGroupMember(groupId, userId);
      } else {
        await api.addGroupMember(groupId, userId);
      }
      const refreshed = await api.getUserGroups(userId);
      setUserCache((prev) => ({ ...prev, [userId]: { ...prev[userId], groups: refreshed } }));
      setActionMessage({ type: 'success', text: 'Group membership updated' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update membership: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handlePermissionChange = async (
    userId: string,
    projectId: string,
    permission: PermissionLevel | null
  ) => {
    setUpdatingKey(`project-${projectId}`);
    try {
      await api.setUserPermission(userId, projectId, permission);
      const refreshed = await api.getUserPermissions(userId);
      setUserCache((prev) => ({ ...prev, [userId]: { ...prev[userId], permissions: refreshed } }));
      setActionMessage({ type: 'success', text: 'Permission updated successfully' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update permission: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const groupItems = (userId: string): ExpandableCellItem[] => {
    const groups = userCache[userId]?.groups ?? [];
    return groups.map((g) => ({ key: g.id, label: g.name }));
  };

  const projectItems = (userId: string): ExpandableCellItem[] => {
    const perms = userCache[userId]?.permissions ?? [];
    return perms.map((p) => ({ key: p.project_id, label: p.project_name, level: p.permission }));
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="tab-content">
      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Groups</th>
            <th>Projects</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const groupsExpanded = expandedCell?.userId === user.id && expandedCell.column === 'groups';
            const projectsExpanded = expandedCell?.userId === user.id && expandedCell.column === 'projects';
            const anyExpanded = groupsExpanded || projectsExpanded;
            return (
              <Fragment key={user.id}>
                <tr>
                  <td>
                    <span className="expandable-row-toggle as-static">
                      {user.first_name} {user.last_name}
                      <span className="expandable-row-detail">{user.email}</span>
                    </span>
                  </td>
                  <td>
                    <ExpandableResourceCell
                      items={groupItems(user.id)}
                      pluralLabel="groups"
                      singularLabel="group"
                      expanded={groupsExpanded}
                      onToggle={() => handleToggleCell(user.id, 'groups')}
                    />
                  </td>
                  <td>
                    <ExpandableResourceCell
                      items={projectItems(user.id)}
                      pluralLabel="projects"
                      singularLabel="project"
                      expanded={projectsExpanded}
                      onToggle={() => handleToggleCell(user.id, 'projects')}
                    />
                  </td>
                </tr>
                {anyExpanded && (
                  <tr className="expansion-row">
                    <td colSpan={3}>
                      {groupsExpanded ? (
                        <GroupMembershipPanel
                          allGroups={allGroups}
                          memberGroupIds={new Set(groupItems(user.id).map((g) => g.key))}
                          onToggle={(groupId, isMember) => handleToggleMembership(user.id, groupId, isMember)}
                          updatingKey={updatingKey}
                        />
                      ) : (
                        <ProjectPermissionsPanel
                          projects={projects}
                          permissionFor={(projectId) =>
                            userCache[user.id]?.permissions?.find((p) => p.project_id === projectId)?.permission ?? null
                          }
                          onChange={(projectId, permission) => handlePermissionChange(user.id, projectId, permission)}
                          updatingKey={updatingKey}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {users.length === 0 && (
            <tr>
              <td colSpan={3} className="empty-state">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GroupMembershipPanel({
  allGroups,
  memberGroupIds,
  onToggle,
  updatingKey,
}: {
  allGroups: Group[];
  memberGroupIds: Set<string>;
  onToggle: (groupId: string, isMember: boolean) => void;
  updatingKey: string | null;
}) {
  if (allGroups.length === 0) {
    return <div className="empty-state">No groups exist yet. Create one under the Groups subtab.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>Group Memberships</h5>
      <div className="checkbox-list">
        {[...allGroups].sort((a, b) => a.name.localeCompare(b.name)).map((group) => {
          const isMember = memberGroupIds.has(group.id);
          const isUpdating = updatingKey === `group-${group.id}`;
          return (
            <label key={group.id} className={`checkbox-item ${isUpdating ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={isMember}
                disabled={isUpdating}
                onChange={() => onToggle(group.id, isMember)}
              />
              <span className="checkbox-label">{group.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ProjectPermissionsPanel({
  projects,
  permissionFor,
  onChange,
  updatingKey,
}: {
  projects: Project[];
  permissionFor: (projectId: string) => PermissionLevel | null;
  onChange: (projectId: string, permission: PermissionLevel | null) => void;
  updatingKey: string | null;
}) {
  const sorted = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );
  if (sorted.length === 0) {
    return <div className="empty-state">No projects available.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>Project Permissions</h5>
      <table className="data-table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Permission</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((project) => (
            <tr key={project.id}>
              <td>{project.name}</td>
              <td>
                <PermissionSelector
                  currentPermission={permissionFor(project.id)}
                  isUpdating={updatingKey === `project-${project.id}`}
                  onPermissionChange={(perm) => onChange(project.id, perm)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
